import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import { DIST_EPSILON } from "geom/core";
import { line2Len, line2Tangent } from "geom/line2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine3Copy, polyLine3ExtendEnd, polyLine3ExtendStart, polyLine3Length } from "geom/polyLine3";
import { ray2DotProject } from "geom/ray2";

interface CheckShedRoofOverlapOptions {
  startIndex: number;
  lowRail: PolyLine2;
  highRail: PolyLine2;
  neighborRoofVolume: GeneratedRoofVolumeLegacy;
}

export function checkShedRoofOverlap({
  startIndex,
  lowRail,
  highRail,
  neighborRoofVolume,
}: CheckShedRoofOverlapOptions): {
  hasOverlap: boolean;
  t1: number;
  t2: number;
} {
  const { rails, startFaces, endFaces, rectangularOverhangs } = neighborRoofVolume;
  const { face: startFrontFace } = startFaces;
  const { face: endFrontFace } = endFaces;
  const { startOverhang, endOverhang } = rectangularOverhangs;
  const { peakRailNoExtension } = rails;

  const refRail = polyLine3Copy(peakRailNoExtension);

  if (startFrontFace) {
    polyLine3ExtendStart(refRail, -Math.min(startOverhang, polyLine3Length(refRail) - 0.1));
  }

  if (endFrontFace) {
    polyLine3ExtendEnd(refRail, -Math.min(endOverhang, polyLine3Length(refRail) - 0.1));
  }

  const originX = highRail[startIndex];
  const originY = highRail[startIndex + 1];
  const destX = lowRail[startIndex];
  const destY = lowRail[startIndex + 1];

  const length = line2Len(originX, originY, destX, destY);
  const [dirX, dirY] = line2Tangent(originX, originY, destX, destY);
  let t1 = ray2DotProject(originX, originY, dirX, dirY, refRail[0], refRail[1]);
  let t2 = ray2DotProject(originX, originY, dirX, dirY, refRail[3], refRail[4]);

  t1 = Math.max(0, Math.min(t1, length));
  t2 = Math.max(0, Math.min(t2, length));

  if (t2 < t1) {
    const tmp = t1;
    t1 = t2;
    t2 = tmp;
  }

  return { hasOverlap: t2 - t1 > DIST_EPSILON, t1, t2 };
}
